import * as React from 'react'

import { useTableSort } from '../../components/Table'
import { FinancialsWidget, getYoYData, useFinancialsTableContext } from '../../components/financials-table'
import {
  FinancialsRowDataType,
  FinancialsRowValue,
  FinancialsSubRowDataType,
} from '../../components/financials-table/types'
import { LineStackedBarChart } from '../../components/line-stacked-bar-chart'
import { RevenueTableFooter } from './RevenueTableFooter'
import { RevenueTableHead } from './RevenueTableHead'
import WidgetHeader from './WidgetHeader'
import { SEGMENT_COLORS } from './constants'
import { CategoryData, RevenueDataEnum, RevenueSettings } from './types'
import { useTableColumns } from './useTableColumns'
import { getSumData } from './utils'

function getSharePctData<T extends FinancialsRowValue>(data: T[], absSumRow: Record<string, number>) {
  return data.reduce<Record<string, number | null>>((acc, { year, value }) => {
    const sharePct = absSumRow[year] > 0 ? (100 / absSumRow[year]) * Math.abs(value ?? 0) : null
    return {
      ...acc,
      [year]: sharePct === null || isNaN(sharePct) ? null : sharePct,
    }
  }, {})
}

function mapData(data: CategoryData) {
  const revenuesArray: FinancialsRowDataType[] = Object.entries(data.revenues)
    .map(([key, revenues]) => ({
      id: key,
      label: key,
      isSelected: true,
      color: SEGMENT_COLORS[0],
      values: revenues
        .map((annualData) => ({
          year: annualData.fiscal_year,
          value: annualData.value,
        }))
        .sort((a, b) => Number.parseInt(a.year) - Number.parseInt(b.year)),
      subData: [],
    }))
    .sort((a, b) => b.values[b.values.length - 1].value - a.values[a.values.length - 1].value)

  return revenuesArray.map((revenue, index) => {
    const yoyData = getYoYData(revenue.values)
    const absSumRow = getSumData(revenuesArray, true)
    const sharePctData = getSharePctData(revenue.values, absSumRow)

    return {
      ...revenue,
      color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
      subData: [
        {
          id: RevenueDataEnum.yoyChange,
          label: 'YoY Growth',
          values: revenue.values.map(({ year }) => ({ year, value: yoyData.change[year] })),
        },
        {
          id: RevenueDataEnum.yoyChangePct,
          label: 'YoY Growth %',
          values: revenue.values.map(({ year }) => ({ year, value: yoyData.changePct[year] })),
        },
        {
          id: RevenueDataEnum.sharePct,
          label: 'Share %',
          values: revenue.values.map(({ year }) => ({ year, value: sharePctData[year] })),
        },
      ].filter(Boolean) as FinancialsSubRowDataType[],
    }
  })
}

type CategoryWidgetProps = {
  category: string
  data: CategoryData
}

export default function CategoryWidget({ category, data }: CategoryWidgetProps) {
  const { settings } = useFinancialsTableContext<RevenueSettings>()
  const [revenueData, setRevenueData] = React.useState(() => mapData(data))
  const yearKeys = React.useMemo(
    () => revenueData[0].values.map(({ year }) => year).sort((a, b) => Number.parseInt(a) - Number.parseInt(b)),
    [revenueData]
  )
  const { columns, subColumns } = useTableColumns(yearKeys)

  const {
    data: sortedData,
    sort,
    changeSort,
  } = useTableSort({
    data: revenueData.map((revenue) => ({
      ...revenue,
      subData: revenue.subData.filter(({ id }) => settings[id as RevenueDataEnum]),
    })),
    columns,
    defaultSort: { column: columns[columns.length - 1], direction: -1 },
  })

  const onVisibilityChange = (id: string, isSelected: boolean) => {
    setRevenueData(
      revenueData.map((item) =>
        item.id === id
          ? {
              ...item,
              isSelected,
            }
          : item
      )
    )
  }

  return (
    <FinancialsWidget
      data={sortedData}
      header={<WidgetHeader category={category} />}
      columns={columns}
      subColumns={subColumns}
      onVisibilityChange={onVisibilityChange}
      tableHead={<RevenueTableHead columns={columns} unit={data.unit} sort={sort} onSortChange={changeSort} />}
      tableFooter={<RevenueTableFooter columns={columns} data={sortedData} />}
    >
      <LineStackedBarChart
        data={sortedData}
        yearKeys={yearKeys}
        chartView={settings.chartView}
        isShowAll={settings.isShowAll}
      />
    </FinancialsWidget>
  )
}
